class LoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.anchor = this.querySelector("a");
    if (this.anchor) this.anchor.addEventListener("click", this.onClick);
  }

  onClick(event) {
    event.preventDefault();
    if (this.loading) return;
    this.loading = true;
    this.anchor.classList.add("loading");
    const spinner = this.anchor.querySelector(".loading__spinner");
    if (spinner) spinner.classList.remove("hidden");

    // paginate.next.url echoes the current request's params, so when this
    // markup arrived via a Section Rendering fetch (a facet change) the href
    // carries section_id - strip it before using the URL for history.
    const nextUrl = new URL(this.anchor.href, window.location.origin);
    nextUrl.searchParams.delete("section_id");
    const sectionId =
      this.dataset.sectionId ||
      document.getElementById("product-grid").dataset.id;
    const fetchUrl = new URL(nextUrl);
    fetchUrl.searchParams.set("section_id", sectionId);

    fetch(fetchUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const doc = new DOMParser().parseFromString(responseText, "text/html");
        const grid = document.getElementById("product-grid");
        const newItems = Array.from(
          doc.querySelectorAll("#product-grid > .grid__item"),
        );
        newItems.forEach((item) => {
          // Appended cards are already in the viewport's flow; running the
          // reveal-on-scroll animation on them would flash them in late.
          if (item.classList.contains("scroll-trigger"))
            item.classList.add("scroll-trigger--cancel");
          item
            .querySelectorAll(".scroll-trigger")
            .forEach((el) => el.classList.add("scroll-trigger--cancel"));
          grid.appendChild(item);
        });

        // Keep the (visually hidden) crawlable pagination in sync with the
        // deepest loaded page, so a no-JS snapshot of this state is coherent.
        const pagination = document.querySelector(
          "#ProductGridContainer .pagination-wrapper",
        );
        const newPagination = doc.querySelector(".pagination-wrapper");
        if (pagination && newPagination) pagination.replaceWith(newPagination);

        // Reflect the deepest loaded page in the URL. replaceState, not
        // pushState: Back should leave the collection, not unwind every
        // load-more click. State keeps facets.js's {searchParams} shape so
        // its popstate handler can restore this view; loadMorePage marks the
        // entry as load-more-built so a bfcache-miss return (which server-
        // renders only page N) knows to re-fetch and prepend pages 1..N-1.
        history.replaceState(
          {
            searchParams: nextUrl.searchParams.toString(),
            loadMorePage: Number(nextUrl.searchParams.get("page")) || 1,
          },
          "",
          nextUrl,
        );

        // Swap in the fetched page's own load-more block: its progress line
        // and next-page href are already correct, and it's absent on the
        // last page.
        const newSelf = doc.querySelector("load-more-button");
        if (newSelf) this.replaceWith(newSelf);
        else this.remove();

        const firstNewLink = newItems[0] && newItems[0].querySelector("a");
        if (firstNewLink) firstNewLink.focus({ preventScroll: true });
      })
      .catch(() => {
        // Fall back to classic pagination navigation.
        window.location.href = nextUrl.toString();
      });
  }
}

customElements.define("load-more-button", LoadMoreButton);
document.documentElement.classList.add("load-more-enabled");

// Returning to a load-more-built history entry without bfcache (e.g. back
// from a product page after the cache was evicted) reloads ?page=N from the
// server, which renders only that page's items. The loadMorePage marker we
// stamped on the entry survives the round trip, so rebuild the cumulative
// list by prepending pages 1..N-1. Direct visits to ?page=N (pagination
// links, crawlers) carry no state and keep classic single-page behavior.
(() => {
  const RESTORE_PAGE_CAP = 10;
  const state = history.state;
  const page = state && state.loadMorePage;
  if (!page || page < 2 || page > RESTORE_PAGE_CAP) return;
  const grid = document.getElementById("product-grid");
  if (!grid || !grid.firstElementChild) return;

  const sectionId = grid.dataset.id;
  const fetches = [];
  for (let p = 1; p < page; p++) {
    const url = new URL(window.location.href);
    url.searchParams.set("section_id", sectionId);
    if (p === 1) url.searchParams.delete("page");
    else url.searchParams.set("page", p);
    fetches.push(
      fetch(url).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      }),
    );
  }

  Promise.all(fetches)
    .then((texts) => {
      const anchor = grid.firstElementChild;
      const anchorTop = anchor.getBoundingClientRect().top;
      const fragment = document.createDocumentFragment();
      texts.forEach((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        doc.querySelectorAll("#product-grid > .grid__item").forEach((item) => {
          if (item.classList.contains("scroll-trigger"))
            item.classList.add("scroll-trigger--cancel");
          item
            .querySelectorAll(".scroll-trigger")
            .forEach((el) => el.classList.add("scroll-trigger--cancel"));
          fragment.appendChild(item);
        });
      });
      grid.prepend(fragment);
      // Keep the viewport anchored on the items the visitor came back to.
      window.scrollBy(0, anchor.getBoundingClientRect().top - anchorTop);
    })
    .catch(() => {
      // Leave the classic single-page view in place.
    });
})();
